import type { TypeId } from '@unipile/db';
import type { ProviderAuthenticationCheckpointInput, ProviderAuthenticationIntentResponse } from '@unipile/types';
import { LoaderIcon } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRequestCheckpoint } from '../../../features/accounts/api/request-checkpoint';
import { useResendCheckpoint } from '../../../features/accounts/api/resend-checkpoint';
import { useSolveCheckpoint } from '../../../features/accounts/api/solve-checkpoint';
import { Button } from '../../shadcn/Button';
import { Field, FieldGroup } from '../../shadcn/Field';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../../shadcn/InputOTP';
import { Spinner } from '../../shadcn/Spinner';
import { WizardStepContainer } from '../containers/WizardStepContainer';

/**
 * A checkpoint that requires a code to be entered. (2FA, OTP, etc.)
 */
export function CodeCheckpoint({
  onSuccess,
  onError,
  intent_id,
  title,
  description,
  length,
  resendButton,
  tryAnotherWayButton,
}: {
  onSuccess: (response: ProviderAuthenticationIntentResponse) => void;
  onError: (error: unknown) => void;
  intent_id: TypeId<'account'>;
  title: string;
  description: string;
  length: number;
  resendButton?: boolean;
  tryAnotherWayButton?: boolean;
}) {
  const form = useForm<ProviderAuthenticationCheckpointInput>();
  const { t } = useTranslation();

  const { mutate: mutateSolveCheckpoint, isPending: isSolveCheckpointPending } = useSolveCheckpoint({
    mutationConfig: {
      onSuccess,
      onError,
    },
  });

  const { mutate: mutateResendCheckpoint, isPending: isResendCheckpointPending } = useResendCheckpoint({
    mutationConfig: { onError },
  });

  const { mutate: mutateRequestCheckpoint, isPending: isRequestCheckpointPending } = useRequestCheckpoint({
    mutationConfig: { onSuccess, onError },
  });

  const onSubmit = (data: ProviderAuthenticationCheckpointInput) => {
    mutateSolveCheckpoint({ data: { ...data, intent_id } });
  };

  return (
    <WizardStepContainer title={title} description={description}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col items-center">
        <FieldGroup>
          <Controller
            control={form.control}
            name="code"
            rules={{ required: true }}
            render={({ field }) => (
              <Field>
                <InputOTP maxLength={length} containerClassName="w-full justify-center" {...field}>
                  <InputOTPGroup className="w-full justify-center">
                    {Array.from({ length }).map((_, index) => (
                      <InputOTPSlot key={index} index={index} />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </Field>
            )}
          />

          <div className="mt-8 flex flex-col gap-4">
            {resendButton && (
              <Button
                onClick={() => mutateResendCheckpoint({ data: { intent_id } })}
                className="w-full cursor-pointer"
                type="button"
                variant={'outline'}
                disabled={isSolveCheckpointPending || isResendCheckpointPending}
              >
                {isResendCheckpointPending ? (
                  <LoaderIcon className="animate-spin" size={20} />
                ) : (
                  t('ProviderWizard.resendOtpChallenge')
                )}
              </Button>
            )}

            <Button
              className="w-full"
              type="submit"
              disabled={isSolveCheckpointPending || isResendCheckpointPending || isRequestCheckpointPending}
            >
              {isSolveCheckpointPending ? <Spinner /> : t('Common.next')}
            </Button>

            {tryAnotherWayButton && (
              <Button
                onClick={() => mutateRequestCheckpoint({ data: { intent_id } })}
                className="w-full cursor-pointer"
                type="button"
                variant={'outline'}
                disabled={isSolveCheckpointPending || isResendCheckpointPending || isRequestCheckpointPending}
              >
                {isRequestCheckpointPending ? (
                  <LoaderIcon className="animate-spin" size={20} />
                ) : (
                  t('ProviderWizard.tryAnotherWay')
                )}
              </Button>
            )}
          </div>
        </FieldGroup>
      </form>
    </WizardStepContainer>
  );
}
